import React, { useState, useEffect, useMemo } from 'react';
import { useAuthorizationState } from './useAuthorizationState';
import { useLocation } from 'react-router';
import { OIDCClient, OIDCServerError } from 'infrastructure/oidc/OIDCClient';
import { cancellable } from 'services/utility/cancellable';

function useAuthorizeRequestUrl(oidcClient: OIDCClient, pkce: boolean, scopes: string[], forwardUrl: string) {
    const { setAuthState } = useAuthorizationState();
    const [url, setUrl] = useState<string>();
    const [error, setError] = useState<unknown>();

    useEffect(() => {
        if (!oidcClient) return;

        return cancellable(async isCanceled => {
            try {
                const { authUrl, state, codeVerifier } = await oidcClient.buildAuthUrl(pkce, scopes);
                if (isCanceled()) return;
                setAuthState({ type: 'AUTH', state, forwardUrl, codeVerifier });
                setUrl(authUrl);
            } catch (e) {
                setError(e);
            }
        });
    }, [forwardUrl, oidcClient, pkce, scopes, setAuthState]);

    return [url, error] as const;
}

export type AuthorizationRequestProps = {
    oidcClient: OIDCClient;
    pkce: boolean;
    scopes?: string[];
};

export const AuthorizationRequest = (props: AuthorizationRequestProps) => {
    const location = useLocation();

    const scopes = useMemo(() => props.scopes ?? [], [props.scopes]);

    const [url, error] = useAuthorizeRequestUrl(props.oidcClient, props.pkce, scopes, location.pathname);
    useEffect(() => {
        if (error === undefined) return;

        if (error instanceof OIDCServerError) {
            if (error.status === 503) {
                console.warn(error.message);
                window.location.href = props.oidcClient.params.issuer;
            }
        }
        throw error;
    }, [error, props.oidcClient.params.issuer]);

    useEffect(() => {
        if (!url) return;
        window.location.href = url;
    }, [url]);

    return (
        <a href={url}>Redirecting...</a>
    );
};
